import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, ChevronDown, Filter } from 'lucide-react';
import ContactCard from '../components/ContactCard';
import AddContactDrawer from '../components/AddContactDrawer';
import { getContacts } from '../api/contacts';
import { fetchUsageSafe } from '../api/usage';

const CONTACTS_PER_PAGE = 25;

function Contacts() {
  const [allContacts, setAllContacts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);

  const navigate = useNavigate();

  const [usage, setUsage] = useState(null);
  const contactLimitReached =
    Number.isFinite(usage?.contactLimit) &&
    usage.contactCount >= usage.contactLimit;

  useEffect(() => {
    let alive = true;

    fetchUsageSafe().then((data) => {
      if (alive) setUsage(data);
    });

    return () => {
      alive = false;
    };
  }, []);


  // Fetch all contacts on mount and on drawer save
  const fetchContacts = async () => {
    try {
      const data = await getContacts();
      setAllContacts(data);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Filter + paginate
  useEffect(() => {
    const filtered = allContacts.filter((contact) =>
      `${contact.firstName || ''} ${contact.lastName || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );

    setTotalContacts(filtered.length);

    const from = (page - 1) * CONTACTS_PER_PAGE;
    const to = from + CONTACTS_PER_PAGE;
    setContacts(filtered.slice(from, to));
  }, [allContacts, searchTerm, page]);

  const totalPages = Math.ceil(totalContacts / CONTACTS_PER_PAGE);

  const handleEdit = (contact) => {
    navigate(`/edit-contact/${contact.id}`, {
      state: { contact },
    });
  };

  return (
    <div className="pt-10 md:pt-0">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">Your contacts</h1>
        <button
          onClick={() => setShowDrawer(true)}
          disabled={contactLimitReached}
          className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition backdrop-blur-md
            ${
              contactLimitReached
                ? 'bg-white/5 text-white/40 border-white/5 cursor-not-allowed'
                : 'bg-white/10 text-white border-white/10 hover:bg-[#AF9B8A]/20'
            }`}
        >
          <PlusCircle className="w-5 h-5" />
          <span>New Contact</span>
        </button>
      </div>

     <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>


      {/* Search */}

      
      
{/* Filters */}
<div className="mb-6 flex flex-col md:flex-row md:items-center md:gap-x-4 gap-y-4">
  <input
    type="text"
    placeholder="Search by name..."
    value={searchTerm}
    onChange={(e) => {
      setSearchTerm(e.target.value);
      setPage(1); // reset to page 1 on search
    }}
    className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 placeholder-gray-400 text-white backdrop-blur-md"
  />

  {/* Dropdowns + Button in one row */}
  <div className="flex flex-wrap gap-4">
    {['Relationship', 'Sort'].map((label, idx) => (
      <div className="relative" key={idx}>
        <select
          className="appearance-none w-44 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
          style={{ cursor: 'pointer' }}
        >
          <option value="">{label}</option>
          {label === 'Relationship' && (
            <>
              <option value="Tenant">Tenant</option>
              <option value="Legal">Legal</option>
              <option value="Agent">Agent</option>
              <option value="Accountant">Accountant</option>
              <option value="Maintence">Maintence</option>
              <option value="Other">Other</option>
            </>
          )}
          {label === 'Sort' && (
            <>
              <option value="az">A - Z</option>
              <option value="za">Z - A</option>
              <option value="latest">Date Created: Latest</option>
              <option value="earliest">Date Created: Earliest</option>
            </>
          )}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
      </div>
    ))}

    <button className="flex items-center gap-2 px-4 py-2 bg-[#AF9B8A]/20 text-white border border-white/10 rounded-xl hover:bg-[#AF9B8A]/40 transition backdrop-blur-md">
      <Filter className="w-4 h-4" />
      <span>Apply Filters</span>
    </button>
  </div>
</div>




      

      {/* Contact Cards */}
      <div className="space-y-4 mb-8 mt-6">
        {contacts.length > 0 ? (
          contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={handleEdit}
            />
          ))
        ) : (
          <div className="text-center text-white opacity-60 py-12">
            No contacts found.
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mb-8">
          <button
            className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/10 hover:bg-[#AF9B8A]/20"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous Page
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={`px-3 py-2 rounded-lg border ${
                page === i + 1 ? 'bg-[#AF9B8A]' : 'bg-white/10'
              } text-white border-white/10 hover:bg-[#AF9B8A]/40`}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/10 hover:bg-[#AF9B8A]/20"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next Page
          </button>
        </div>
      )}

      {/* Drawer */}
      {showDrawer && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setShowDrawer(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />

          {/* Drawer Panel */}
          <div
            className="relative w-full max-w-xl h-full bg-[#1e1e1e] border-l border-white/10 transform transition-transform duration-300 ease-out translate-x-0"
            onClick={(e) => e.stopPropagation()}
          >
            <AddContactDrawer
              onSave={() => {
                setShowDrawer(false);
                fetchContacts();
              }}
              onClose={() => setShowDrawer(false)}
              properties={[]} // Optional: pass actual properties if needed
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Contacts;
